import type { EquipmentInstance, Player } from './types';

// ── Run-Streak engagement multiplier ──────────────────────────
// Rolling 30-minute window. ONLY manual / endless / boss runs push entries
// — Farm clicks and Auto-Farmer ticks are deliberately excluded so the
// bonus stays tied to actual combat-watching engagement.
//
// Tier thresholds are calibrated to mid-game manual-run pace (~90 s per
// vault clear including the claim step). At that pace a focused 30-min
// session yields ~16-20 runs, so the cap is hit comfortably without
// requiring frame-perfect speedrunning. Level-5 player at slower pace
// (~120 s/run) still reaches Cap with ~18 min of focused play.
export const RUN_STREAK_WINDOW_MS = 30 * 60_000;
const RUN_STREAK_TIERS: ReadonlyArray<readonly [number, number]> = [
  [0,  1.00],
  [2,  1.05],
  [4,  1.10],
  [7,  1.20],
  [11, 1.30],
];

/** Returns the recent-run count after pruning entries older than the window. */
export function activeStreakCount(player: Player, now: number = Date.now()): number {
  if (!player.runStreak) return 0;
  const cutoff = now - RUN_STREAK_WINDOW_MS;
  return player.runStreak.filter((t) => t > cutoff).length;
}

/** Compute the streak multiplier from a run count. Pure / cheap; no
 *  Player-state mutation. */
export function streakMultFromCount(count: number): number {
  let mult = 1.0;
  for (const [minRuns, m] of RUN_STREAK_TIERS) {
    if (count >= minRuns) mult = m;
  }
  return mult;
}

/** Convenience: current multiplier for the given player. */
export function currentStreakMult(player: Player, now: number = Date.now()): number {
  return streakMultFromCount(activeStreakCount(player, now));
}

/** Push a fresh timestamp into the streak history + prune stale entries.
 *  Returns a NEW Player object — callers integrate it via store.setState
 *  the same way as other player mutations. */
export function pushStreakEntry(player: Player, now: number = Date.now()): Player {
  const cutoff = now - RUN_STREAK_WINDOW_MS;
  const trimmed = (player.runStreak ?? []).filter((t) => t > cutoff);
  trimmed.push(now);
  // Hard cap on length so a 30-min spam-farm can't bloat the array
  // (worst case: 30 farms at 60 s cooldown = 30 entries, well within budget).
  return { ...player, runStreak: trimmed.slice(-32) };
}

import {
  GAME_VERSION,
  MAX_LEVEL,
  STARTING_CLEAN_CODE,
  STARTING_CORRUPTED_DATA,
  STARTING_CREDITS,
  STARTING_INVENTORY_SLOTS,
  STARTING_LEVEL,
  STARTING_NEON_GEMS,
  STARTING_PREMIUM,
  xpRequiredForLevel,
} from './constants';

/**
 * Generates a UUIDv4. Equipment + listing IDs go to Supabase as `uuid` columns,
 * so we always emit a real UUID. The prefix parameter is kept for API compat
 * with existing call sites but is now ignored.
 */
export function randomId(_prefix: string = 'p'): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback: RFC4122-ish v4 (only used when crypto is unavailable, e.g. very old browsers)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True if `id` looks like a valid UUID (compatible with Postgres `uuid` column). */
export function isUuid(id: string): boolean {
  return UUID_RE.test(id);
}

export function createPlayer(name: string, now: number = Date.now()): Player {
  return {
    id: randomId('p'),
    userId: null,
    characterIndex: 0,
    characterClass: 'hollow_knight',
    name,
    level: STARTING_LEVEL,
    xp: 0,
    gameVersion: GAME_VERSION,
    credits: STARTING_CREDITS,
    premium: STARTING_PREMIUM,
    cleanCode: STARTING_CLEAN_CODE,
    corruptedData: STARTING_CORRUPTED_DATA,
    neonGems: STARTING_NEON_GEMS,
    heat: 0,
    overheatedUntil: 0,
    inventory: {
      slots: [],
      maxSlots: STARTING_INVENTORY_SLOTS,
    },
    equipmentStash: [],
    equipment: {},
    autoMiners: {},
    // Seed free Scrap-Parser so the CD → Clean-Code pipeline works from turn 1.
    decrypters: { scrap_parser: { level: 1, buffer: 0, storageMultiplier: 1 } },
    boosts: [],
    lastTickAt: now,
    currentHp: 50,
    createdAt: now,
    updatedAt: now,
    highestVaultTier: 0,
    skillSlots: [null, null],
    unlockedSkills: [],
    marketManipEndsAt: 0,
    autoFarmerVaultId: null,
    lastAutoFarmAt: 0,
    avatarColor: 'blue',
    research: {},
    skillWebNodes: {},
    lastBossKillAt: {},
    ascensionFragments: 0,
    legendaryPity: 0,
    // Retention foundation (P1) — start empty, populated by gameplay events.
    unlockedCosmetics: {},
    achievements: {},
    dailyQuest: null,
    tutorialStep: 0,
    // Augment-Tree (P2.A) — all branches at 0, sequential unlock via CD spend.
    augments: { survivor: 0, aggressor: 0, hunter: 0, engineer: 0, mystic: 0 },
    // Aspect-System (P2.B) — Bulwark auto-unlock + L1 + primary slot so the
    // player has a valid loadout from turn 1.
    aspects: {
      primary: 'bulwark',
      secondary: null,
      levels: { bulwark: 1, pyromant: 0, stormcaller: 0, vampire: 0, phantom: 0, engineer: 0 },
      unlocked: ['bulwark'],
    },
    unlockedActiveSkills: ['frost_nova'],
    activeSkillId: 'frost_nova',
  };
}

/**
 * Hybrid migration v1 → v2: keep items/level/equipment-stash/ascended-status,
 * reset currencies/research/skill-slots/miners. Idempotent — running again on a
 * v2 player is a no-op.
 */
export function migrateToV2(p: Player, now: number = Date.now()): Player {
  if ((p.gameVersion ?? 1) >= 2) return p;

  const migratedStash = p.equipmentStash.map(migrateInstance);
  const migratedEquipment: typeof p.equipment = {};
  for (const [slot, inst] of Object.entries(p.equipment)) {
    if (!inst) continue;
    migratedEquipment[slot as keyof typeof p.equipment] = migrateInstance(inst);
  }

  return {
    ...p,
    gameVersion: GAME_VERSION,
    // Reset currencies — Hybrid migration wipes economy
    credits: 0,
    premium: 0,
    cleanCode: STARTING_CLEAN_CODE,
    corruptedData: STARTING_CORRUPTED_DATA,
    neonGems: STARTING_NEON_GEMS,
    heat: 0,
    overheatedUntil: 0,
    // Reset progression layers — fresh Skill-Web start
    research: {},
    skillWebNodes: {},
    skillSlots: [null, null],
    autoMiners: {},
    // Seed free Scrap-Parser so the CD → Clean-Code pipeline works from turn 1.
    decrypters: { scrap_parser: { level: 1, buffer: 0, storageMultiplier: 1 } },
    marketManipEndsAt: 0,
    // Wipe inventory resources — Decrypter pipeline produces nano_chip etc. anew
    inventory: { slots: [], maxSlots: p.inventory.maxSlots ?? STARTING_INVENTORY_SLOTS },
    // Preserve: equipment stash + equipped items (with ascended → ascensionTier)
    equipmentStash: migratedStash,
    equipment: migratedEquipment,
    updatedAt: now,
  };
}

/** Legacy `ascended:true` → `ascensionTier:1, glitchPerks:[]`. */
function migrateInstance(inst: EquipmentInstance): EquipmentInstance {
  if (inst.ascensionTier !== undefined) return inst;
  const tier = inst.ascended ? 1 : 0;
  return {
    ...inst,
    ascensionTier: tier,
    glitchPerks: tier > 0 ? [] : undefined,
  };
}

export function touch(player: Player, now: number = Date.now()): Player {
  return { ...player, updatedAt: now };
}

export function addXp(player: Player, amount: number, now: number = Date.now()): Player {
  if (amount <= 0) return player;

  let level = player.level;
  let xp = player.xp + amount;

  while (level < MAX_LEVEL && xp >= xpRequiredForLevel(level + 1)) {
    xp -= xpRequiredForLevel(level + 1);
    level += 1;
  }

  if (level >= MAX_LEVEL) {
    xp = 0;
  }

  return { ...player, level, xp, updatedAt: now };
}
